import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Building2, GitBranch, MessageSquareWarning } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { fetchCalls } from '../api';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#0ea5e9', '#7c3aed', '#64748b'];

const FUNNEL_STAGES = [
  {
    key: 'target_client',
    title: 'Целевые клиентские звонки',
    description: 'Клиенты, которые обсуждают курс, проживание, бронь, стоимость, документы или процедуры',
    filter: { call_type: 'target_client' }
  },
  {
    key: 'unfinished',
    title: 'Интерес или условия обсуждены',
    description: 'Клиент обозначил потребность или получил информацию, но договоренность еще не подтверждена',
    filter: { booking_outcome: 'unfinished' }
  },
  {
    key: 'pending_specific_next_step',
    title: 'Следующий шаг зафиксирован',
    description: 'Перезвон, WhatsApp, визит, ожидание уточнения или согласование',
    filter: { booking_outcome: 'pending_specific_next_step' }
  },
  {
    key: 'achieved',
    title: 'Бронь / запись подтверждены',
    description: 'Бронь, заезд, перенос записи или другая клиентская договоренность подтверждены',
    filter: { booking_outcome: 'achieved' }
  }
];

const OBJECTION_LABELS = {
  price_too_high: 'Дорого',
  no_suitable_dates_or_rooms: 'Нет подходящих дат или номеров',
  course_too_long_or_wrong_format: 'Не подходит формат курса',
  certificate_or_documents_issue: 'Проблема со справками / документами',
  service_unavailable: 'Нужная услуга недоступна',
  wants_to_think_or_compare: 'Хочет подумать / сравнить',
  callback_or_slow_followup: 'Нужен перезвон / ожидание уточнений',
  needs_other_person_decision: 'Нужно согласовать',
  not_interested: 'Не интересно',
  other: 'Другое'
};

const COMPLAINT_LABELS = {
  no_response_or_followup: 'Нет ответа или продолжения',
  room_or_facility_problem: 'Проблема в номере / корпусе',
  medical_or_procedure_service: 'Медицинский / процедурный сервис',
  booking_or_availability: 'Бронь, места или расписание',
  price_or_documents: 'Цена или документы',
  staff_communication: 'Коммуникация сотрудников',
  other: 'Другая жалоба'
};

const normalizeDateRange = (dateRange) => {
  if (!dateRange?.from || !dateRange?.to) return null;
  return {
    date_from: format(dateRange.from, 'yyyy-MM-dd'),
    date_to: format(dateRange.to, 'yyyy-MM-dd')
  };
};

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return [value].filter(Boolean);
};

const formatPercent = (value, total) => {
  if (!total) return '0%';
  return `${((value * 100) / total).toFixed(1)}%`;
};

const summarize = (items, keyGetter, labelGetter, limit = 8) => {
  const counts = new Map();
  const labels = new Map();
  items.forEach((item) => {
    const key = keyGetter(item);
    if (!key) return;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    labels.set(key, labelGetter(item, key));
  });

  const total = items.length;
  return Array.from(counts.entries())
    .map(([key, count]) => ({
      key,
      label: labels.get(key) ?? key,
      count,
      total,
      valueLabel: `${count} (${formatPercent(count, total)})`
    }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'ru'))
    .slice(0, limit);
};

const MiniBarChart = ({ title, description, icon: Icon, data, onSelect }) => {
  const maxCount = Math.max(1, ...data.map((item) => item.count));
  return (
    <Card>
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
        ) : (
          <div className="space-y-3 rounded-md bg-muted/30 p-3">
            {data.map((item, index) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onSelect?.(item)}
                className="block w-full rounded-md px-1 py-1 text-left transition-colors hover:bg-background/70 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <div className="mb-1 flex items-start justify-between gap-3 text-xs">
                  <span className="min-w-0 text-muted-foreground">{item.label}</span>
                  <span className="shrink-0 font-semibold text-foreground">{item.valueLabel}</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(4, (item.count * 100) / maxCount)}%`,
                      backgroundColor: COLORS[index % COLORS.length]
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BookingFunnelCard = ({ items, onOpenCallJournal }) => {
  const targetCalls = items.filter((item) => item.callType === 'target_client');
  const counts = {
    target_client: targetCalls.length,
    unfinished: targetCalls.filter((item) => item.bookingOutcome === 'unfinished').length,
    pending_specific_next_step: targetCalls.filter((item) => item.bookingOutcome === 'pending_specific_next_step').length,
    achieved: targetCalls.filter((item) => item.bookingOutcome === 'achieved').length
  };
  const maxCalls = Math.max(1, ...FUNNEL_STAGES.map((stage) => counts[stage.key] ?? 0));
  const conversion = counts.target_client ? (counts.achieved * 100) / counts.target_client : 0;

  return (
    <Card>
      <CardHeader className="space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          Воронка бронирования
        </CardTitle>
        <CardDescription>
          Конверсия в подтвержденную бронь или запись: {conversion.toFixed(1)}%
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {FUNNEL_STAGES.map((stage) => {
          const calls = counts[stage.key] ?? 0;
          const share = counts.target_client ? (calls * 100) / counts.target_client : 0;
          return (
            <button
              key={stage.key}
              type="button"
              onClick={() => onOpenCallJournal?.(stage.filter)}
              className="w-full rounded-md border bg-muted/20 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{stage.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{stage.description}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-semibold">{calls.toLocaleString('ru-RU')}</div>
                  <div className="text-xs text-muted-foreground">{share.toFixed(1)}%</div>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                <div
                  className="h-2 rounded-full bg-blue-600"
                  style={{ width: `${Math.max(4, Math.min(100, (calls * 100) / maxCalls))}%` }}
                />
              </div>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

const BelyMedvedDashboardWidgets = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);

  const params = useMemo(() => {
    const range = normalizeDateRange(dateRange);
    if (!range) return null;
    return {
      ...range,
      call_type: callType === 'all' ? undefined : callType,
      limit: 500
    };
  }, [callType, dateRange]);

  const load = useCallback(async () => {
    if (!params || isFetching.current) return;
    setError(null);
    isFetching.current = true;
    try {
      const response = await fetchCalls(params);
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch {
      setError('Не удалось загрузить срезы Белого Медведя.');
      setItems([]);
    } finally {
      isFetching.current = false;
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const unfinishedTargetCalls = useMemo(
    () => items.filter((item) => item.callType === 'target_client' && item.bookingOutcome !== 'achieved'),
    [items]
  );

  const objections = useMemo(
    () => summarize(
      unfinishedTargetCalls.flatMap((item) => asArray(item.objections).map((objection) => ({ objection }))),
      (item) => item.objection,
      (item, key) => OBJECTION_LABELS[key] ?? key,
      8
    ),
    [unfinishedTargetCalls]
  );

  const complaints = useMemo(
    () => summarize(
      items.flatMap((item) => asArray(item.negativeSubCategories).map((complaint) => ({ complaint }))),
      (item) => item.complaint,
      (item, key) => COMPLAINT_LABELS[key] ?? key,
      8
    ),
    [items]
  );

  const lines = useMemo(
    () => summarize(
      items.filter((item) => item.callType === 'target_client'),
      (item) => item.employeeName,
      (item) => item.employeeName ?? 'Линия не указана',
      8
    ),
    [items]
  );

  return (
    <div className="grid gap-6">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <BookingFunnelCard items={items} onOpenCallJournal={onOpenCallJournal} />

      <div className="grid gap-6 lg:grid-cols-3">
        <MiniBarChart
          title="Почему не покупают"
          description="Возражения в незакрытых целевых звонках"
          icon={AlertTriangle}
          data={objections}
          onSelect={(item) => onOpenCallJournal?.({ objection: item.key, funnel_stage: 'refusal' })}
        />
        <MiniBarChart
          title="Жалобы гостей"
          description="Только клиентские и гостевые жалобы"
          icon={MessageSquareWarning}
          data={complaints}
          onSelect={(item) => onOpenCallJournal?.({ negative_sub_category: item.key })}
        />
        <MiniBarChart
          title="Линии и сотрудники"
          description="Целевые клиентские звонки по внутренней линии"
          icon={Building2}
          data={lines}
          onSelect={(item) => onOpenCallJournal?.({ department: item.key, call_type: 'target_client' })}
        />
      </div>
    </div>
  );
};

export default BelyMedvedDashboardWidgets;
